import { Loader2, RefreshCw, ShieldAlert, Trash2, UserX, UserCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { api, type UserRow } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';

export function UsersPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pending, setPending] = useState<number | null>(null);
  const [toDelete, setToDelete] = useState<UserRow | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      setRows(await api.listUsers());
    } catch (err) {
      toast.error('Failed to load users', { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const patch = async (u: UserRow, changes: Partial<Pick<UserRow, 'role' | 'disabled'>>) => {
    setPending(u.id);
    try {
      const res = await api.updateUser(u.id, changes);
      setRows((prev) => prev?.map((r) => (r.id === u.id ? res.user : r)) ?? null);
      const bits: string[] = [];
      if (changes.role) bits.push(`role → ${changes.role}`);
      if (changes.disabled !== undefined) bits.push(changes.disabled ? 'disabled' : 're-enabled');
      toast.success(`${u.email}: ${bits.join(', ')}`, {
        description: res.killed ? `Signed out ${res.killed} active session(s)` : undefined,
      });
    } catch (err) {
      toast.error('Update failed', { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setPending(null);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setPending(toDelete.id);
    try {
      await api.deleteUser(toDelete.id);
      setRows((prev) => prev?.filter((r) => r.id !== toDelete.id) ?? null);
      toast.success(`Deleted ${toDelete.email}`);
      setToDelete(null);
    } catch (err) {
      toast.error('Delete failed', { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setPending(null);
    }
  };

  if (me?.role !== 'admin') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Admins only
          </CardTitle>
          <CardDescription>You need admin privileges to view this page.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              {rows?.length ?? 0} user{rows?.length === 1 ? '' : 's'}. Role changes and disables
              also sign the target out of any active sessions.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" aria-label="Refresh" onClick={load} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {rows === null ? (
            <div className="grid place-items-center p-8 text-sm text-muted-foreground">
              <Loader2 className="mb-2 h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No users yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Email</th>
                    <th className="px-4 py-2 text-left font-medium">Name</th>
                    <th className="px-4 py-2 text-left font-medium">Role</th>
                    <th className="px-4 py-2 text-left font-medium">Last login</th>
                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const isMe = u.id === me!.id;
                    const isPending = pending === u.id;
                    return (
                      <tr key={u.id} className={`border-b last:border-b-0 ${u.disabled ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium">{u.email}</div>
                          {isMe && <div className="text-[10px] uppercase tracking-wider text-primary">you</div>}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{u.display_name || '—'}</td>
                        <td className="px-4 py-2">
                          <Select
                            value={u.role}
                            onValueChange={(v) => patch(u, { role: v as UserRow['role'] })}
                            disabled={isMe || isPending}
                          >
                            <SelectTrigger className="h-8 w-[110px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'never'}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            {u.disabled ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => patch(u, { disabled: 0 })}
                                disabled={isMe || isPending}
                                title="Re-enable"
                              >
                                <UserCheck className="h-4 w-4" /> Enable
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => patch(u, { disabled: 1 })}
                                disabled={isMe || isPending}
                                title="Disable (signs them out)"
                              >
                                <UserX className="h-4 w-4" /> Disable
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setToDelete(u)}
                              disabled={isMe || isPending}
                              title="Delete"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              This removes <strong>{toDelete?.email}</strong> and signs them out of every session.
              Their history in existing rooms is preserved but they can only rejoin if you allow
              their email domain again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={pending === toDelete?.id}
            >
              {pending === toDelete?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
